import { Button, Card, CardHeader, useToast } from "@helmr/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { postAdjustment, postAdvance } from "../../api/payroll";
import { useServices } from "../../app/services";
import { useProfiles } from "./hooks";
import { Field, Gate, MoneyInput, css as s, errorText, isDate, isMonth, monthOf, parseMoneyInput, shiftMonth, useIntentKey, useScope } from "./kit";

/** Advances and signed corrections. The API has no list route for either, so this tab records and confirms; recoveries show on payslips. */
export function AdvancesTab() {
  const sc = useScope();
  const q = useProfiles(sc);
  const canAdd = sc.ctx.has("payroll.advances");
  return (
    <>
      <Gate query={q} what="staff">
        {(d) => !canAdd ? <Card><div className={s.pad}><p className={s.note}>You can view payroll but not add advances or corrections.</p></div></Card> : (
          <>
            <AdvanceForm staff={d.profiles.map((e) => ({ id: e.staffId, name: e.name }))} />
            <AdjustmentForm staff={d.profiles.map((e) => ({ id: e.staffId, name: e.name }))} />
          </>
        )}
      </Gate>
    </>
  );
}

type Staff = Array<{ id: string; name: string }>;

function AdvanceForm({ staff }: { staff: Staff }) {
  const sc = useScope();
  const { client } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const intent = useIntentKey();
  const [staffId, setStaffId] = useState("");
  const [amount, setAmount] = useState("");
  const [per, setPer] = useState("");
  const [given, setGiven] = useState(sc.today);
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const amt = parseMoneyInput(amount, sc.currency, sc.locale);
  const rec = parseMoneyInput(per, sc.currency, sc.locale);
  const errs = {
    amount: amount === "" ? null : amt === null || amt < 1 ? "Enter an amount above zero." : null,
    per: per === "" ? null : rec === null || rec < 1 ? "Enter an amount above zero." : amt !== null && rec > amt ? "Recovery per month cannot be more than the advance." : null,
    given: isDate(given) ? null : "Use a date like 2026-11-02.",
  };
  const ready = !!staffId && amt !== null && amt >= 1 && rec !== null && rec >= 1 && !errs.per && !errs.given;
  const save = useMutation({
    mutationFn: () => postAdvance(client, { staffId, amount: amt!, givenOn: given, recoveryPerMonth: rec!, ...(note.trim() ? { note: note.trim() } : {}) }, intent.key()),
    onSuccess: async () => { intent.renew(); setErr(null); setAmount(""); setPer(""); setNote(""); toast.show("Advance recorded"); await qc.invalidateQueries({ queryKey: ["payroll"] }); },
    onError: (e) => setErr(errorText(e)),
  });
  return (
    <Card aria-label="Salary advance">
      <CardHeader title="Salary advance" subtitle="Recovered from payslips a little each month." />
      <div className={s.body}>
        <Field label="Staff">{(a) => <select {...a} className={s.input} value={staffId} onChange={(e) => setStaffId(e.target.value)}><option value="">Choose staff</option>{staff.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>}</Field>
        <div className={s.grid2}>
          <Field label="Advance amount" error={errs.amount}>{(a) => <MoneyInput {...a} value={amount} onChange={setAmount} />}</Field>
          <Field label="Recovered per month" error={errs.per}>{(a) => <MoneyInput {...a} value={per} onChange={setPer} />}</Field>
        </div>
        <Field label="Given on" error={errs.given}>{(a) => <input {...a} className={s.input} value={given} onChange={(e) => setGiven(e.target.value)} placeholder="YYYY-MM-DD" />}</Field>
        <Field label="Note" hint="Optional">{(a) => <input {...a} className={s.input} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />}</Field>
        {err ? <p className={s.formErr} role="alert">{err}</p> : null}
        <div><Button variant="primary" disabled={!ready} busy={save.isPending} onClick={() => save.mutate()}>Record advance</Button></div>
      </div>
    </Card>
  );
}

function AdjustmentForm({ staff }: { staff: Staff }) {
  const sc = useScope();
  const { client } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const intent = useIntentKey();
  const [staffId, setStaffId] = useState("");
  const [amount, setAmount] = useState("");
  const [source, setSource] = useState(shiftMonth(monthOf(sc.today), -1));
  const [target, setTarget] = useState(monthOf(sc.today));
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const amt = parseMoneyInput(amount, sc.currency, sc.locale, true);
  const errs = {
    amount: amount === "" ? null : amt === null || amt === 0 ? "Enter a non-zero amount; use a minus sign to deduct." : null,
    source: isMonth(source) ? null : "Use YYYY-MM.",
    target: !isMonth(target) ? "Use YYYY-MM." : isMonth(source) && target <= source ? "Must be after the month being corrected." : null,
  };
  const ready = !!staffId && amt !== null && amt !== 0 && !errs.source && !errs.target && reason.trim() !== "";
  const save = useMutation({
    mutationFn: () => postAdjustment(client, { staffId, targetMonth: target, sourceMonth: source, amount: amt!, reason: reason.trim() }, intent.key()),
    onSuccess: async () => { intent.renew(); setErr(null); setAmount(""); setReason(""); toast.show("Correction recorded"); await qc.invalidateQueries({ queryKey: ["payroll"] }); },
    onError: (e) => setErr(errorText(e)),
  });
  return (
    <Card aria-label="Correction">
      <CardHeader title="Correction to a closed month" subtitle="A signed line on a later month. Nothing in the closed month changes." />
      <div className={s.body}>
        <Field label="Staff">{(a) => <select {...a} className={s.input} value={staffId} onChange={(e) => setStaffId(e.target.value)}><option value="">Choose staff</option>{staff.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>}</Field>
        <div className={s.grid2}>
          <Field label="Month being corrected" error={errs.source}>{(a) => <input {...a} className={s.input} value={source} onChange={(e) => setSource(e.target.value)} placeholder="YYYY-MM" />}</Field>
          <Field label="Apply on month" error={errs.target}>{(a) => <input {...a} className={s.input} value={target} onChange={(e) => setTarget(e.target.value)} placeholder="YYYY-MM" />}</Field>
        </div>
        <Field label="Amount" error={errs.amount} hint="Plus adds to pay, minus deducts">{(a) => <MoneyInput {...a} value={amount} onChange={setAmount} />}</Field>
        <Field label="Reason">{(a) => <input {...a} className={s.input} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />}</Field>
        {err ? <p className={s.formErr} role="alert">{err}</p> : null}
        <div><Button variant="primary" disabled={!ready} busy={save.isPending} onClick={() => save.mutate()}>Record correction</Button></div>
      </div>
    </Card>
  );
}
